import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Trash2, Video } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useVideoGuides, useDeleteVideoGuide, VideoGuide } from "@/hooks/use-video-guides";
import { useSimpleRole } from "@/hooks/use-simple-role";

export const VideoGuidesList: React.FC = () => {
  const { data: videos, isLoading } = useVideoGuides();
  const { isSystemAdmin } = useSimpleRole();
  const deleteMutation = useDeleteVideoGuide();
  const [toDelete, setToDelete] = useState<VideoGuide | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 w-3/4 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="aspect-video w-full bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No videos yet</h3>
            <p className="text-muted-foreground">
              {isSystemAdmin
                ? "Add your first Vimeo video to get started."
                : "Check back later for video guides."}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        {videos.map((video) => (
          <Card key={video.id} className="overflow-hidden">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 flex-1">
                  <CardTitle className="text-lg text-left">{video.title}</CardTitle>
                  {video.description && (
                    <CardDescription className="text-left">{video.description}</CardDescription>
                  )}
                </div>
                {isSystemAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToDelete(video)}
                    className="text-destructive hover:text-destructive"
                    aria-label="Delete video"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <AspectRatio ratio={16 / 9} className="bg-muted rounded overflow-hidden">
                <iframe
                  src={`https://player.vimeo.com/video/${video.vimeo_id}`}
                  title={video.title}
                  className="w-full h-full"
                  frameBorder={0}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  allowFullScreen
                />
              </AspectRatio>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{toDelete?.title}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) {
                  deleteMutation.mutate(toDelete.id);
                  setToDelete(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};